import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useI18n } from "../../i18n";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { StatusBadge } from "../../components/app/widgets";

function Stat({ label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-border bg-card p-5">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-bold text-[hsl(217_63%_15%)] font-display">{value}</div>
    </div>
  );
}

export default function EngagementSummary() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const { orgId } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const targetId = orgId || user?.organization_id;

  useEffect(() => {
    if (!targetId) return;
    api.get(`/orgs/${targetId}/summary`).then((r) => setD(r.data)).catch(() => {});
  }, [targetId]);

  if (!targetId) return <div className="text-muted-foreground">{t("no_data")}</div>;
  if (!d) return <div className="text-muted-foreground">{t("loading")}</div>;

  const org = d.organization;
  const svc = (c) => (lang === "ar" ? c.service_label_ar : c.service_label_en);

  return (
    <div className="space-y-6 gc-fade-up" data-testid="engagement-summary">
      <div>
        <h1 className="font-display text-2xl font-bold text-[hsl(217_63%_15%)]">{org.name}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("nav_summary")} — {org.tin || "—"} · {org.sector || org.sector_type}</p>
      </div>

      <div className="grid sm:grid-cols-4 gap-4">
        <Stat testid="sum-plan" label={t("plan_label")} value={d.plan ? (lang === "ar" ? d.plan.name_ar : d.plan.name_en) : (org.plan_id || "—")} />
        <Stat testid="sum-cases" label={t("nav_cases")} value={d.totals.cases} />
        <Stat testid="sum-hours" label={t("total_hours")} value={`${d.totals.hours} ${t("hours")}`} />
        <Stat testid="sum-spend" label={t("total_spend")} value={`${d.totals.spend.toLocaleString()} SAR`} />
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="px-4 py-3 text-sm font-semibold text-[hsl(217_63%_15%)] border-b border-border">{t("nav_cases")}</div>
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-muted-foreground text-xs"><tr>
            <th className="text-start px-4 py-3 font-medium">{t("reference")}</th>
            <th className="text-start px-4 py-3 font-medium">{t("service")}</th>
            <th className="text-start px-4 py-3 font-medium">{t("status")}</th>
            <th className="text-start px-4 py-3 font-medium">{t("hours_worked")}</th>
          </tr></thead>
          <tbody>
            {d.cases.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">{t("no_data")}</td></tr>}
            {d.cases.map((c) => (
              <tr key={c.id} data-testid={`sum-case-${c.id}`} onClick={() => nav(`/app/cases/${c.id}`)} className="border-t border-border hover:bg-secondary/40 cursor-pointer">
                <td className="px-4 py-3 font-mono text-xs font-medium text-[hsl(217_63%_15%)]">{c.reference}</td>
                <td className="px-4 py-3">{svc(c)}</td>
                <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                <td className="px-4 py-3 font-mono font-bold text-[hsl(158_64%_28%)]">{c.hours} {t("hours")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="px-4 py-3 text-sm font-semibold text-[hsl(217_63%_15%)] border-b border-border">{t("invoices")}</div>
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-muted-foreground text-xs"><tr>
            <th className="text-start px-4 py-3 font-medium">{t("plan_label")}</th>
            <th className="text-start px-4 py-3 font-medium">{t("amount")}</th>
            <th className="text-start px-4 py-3 font-medium">{t("status")}</th>
          </tr></thead>
          <tbody>
            {d.invoices.length === 0 && <tr><td colSpan={3} className="px-4 py-6 text-center text-muted-foreground">{t("no_data")}</td></tr>}
            {d.invoices.map((inv) => (
              <tr key={inv.id} className="border-t border-border">
                <td className="px-4 py-3">{inv.plan_id}</td>
                <td className="px-4 py-3 font-mono">{inv.amount?.toLocaleString()} {inv.currency}</td>
                <td className="px-4 py-3"><span className="text-xs text-[hsl(158_64%_28%)]">{inv.status === "paid" ? t("paid") : inv.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
